/** Reopen a Color, Please project. The saved palette is complete without the
 * source pixels; images remain local and are never smuggled into the file. */

import { useRef, useState } from "react";

import { copy, parseProjectFile, type ColorPleaseProjectFile, type SharedBrief } from "@core/index.js";

import { useAnnounce } from "../app/announcer";
import { useRouter } from "../app/router";
import { useStudio } from "../app/store";
import { typeColourRunFromOutput } from "../wiring/engines";
import { REMEMBERED_PROJECT_KEY } from "../wiring/exports";

type PagePhase =
  | { kind: "idle" }
  | { kind: "error"; message: string }
  | { kind: "ready"; project: ColorPleaseProjectFile };

const guided = (project: ColorPleaseProjectFile): boolean => {
  const answers = project.answers.colour;
  return answers.sourceRelationship !== "unknown"
    || answers.baseMode !== "decide"
    || answers.dataNeed !== "unknown";
};

const viewingFromDelivery = (delivery: ColorPleaseProjectFile["answers"]["colour"]["delivery"]): SharedBrief["viewingContexts"] => {
  if (delivery === "live_room") return ["projector"];
  if (delivery === "screen_share" || delivery === "sent_pdf") return ["laptop"];
  if (delivery === "phone") return ["phone"];
  return ["mixedUnknown"];
};

const densityFromLoad = (load: ColorPleaseProjectFile["answers"]["colour"]["contentLoad"]): SharedBrief["density"] => {
  if (load === "spare") return "sparse";
  if (load === "balanced") return "moderate";
  if (load === "dense") return "dense";
  return "varied";
};

const briefFromProject = (project: ColorPleaseProjectFile): SharedBrief => ({
  route: "colour",
  colourPath: guided(project) ? "guided" : "quick",
  viewingContexts: viewingFromDelivery(project.answers.colour.delivery),
  density: densityFromLoad(project.answers.colour.contentLoad),
  sourceRelationship: project.answers.colour.sourceRelationship,
  baseMode: project.answers.colour.baseMode,
  dataNeed: project.answers.colour.dataNeed,
  ...(project.answers.colour.dataCount !== undefined ? { dataCount: project.answers.colour.dataCount } : {}),
  ...(project.answers.colour.divergingMidpoint ? { divergingMidpoint: project.answers.colour.divergingMidpoint } : {}),
  colourDeliveryOverride: project.answers.colour.delivery,
  previewCopy: project.direction.previewCopy,
});

export function ProjectOpenPage() {
  const { dispatch } = useStudio();
  const { navigate } = useRouter();
  const announce = useAnnounce();
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<PagePhase>({ kind: "idle" });
  const [remembered, setRemembered] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(REMEMBERED_PROJECT_KEY);
    } catch {
      return null;
    }
  });

  const restore = (project: ColorPleaseProjectFile) => {
    const colourRun = typeColourRunFromOutput({
      ...project.direction.colour,
      companionSystems: project.direction.colour.companionSystems ?? [],
    });
    dispatch({
      type: "projectLoaded",
      brief: briefFromProject(project),
      colourRun,
      selectedPaletteId: project.direction.selected.paletteSystemId ?? colourRun.recommendation.recommendedSystemId,
      projectId: project.projectId,
      projectName: project.direction.name,
    });
    announce(`${project.direction.name}. Your palette is back; nothing left this browser.`);
    navigate("/studio");
  };

  const openText = (text: string) => {
    try {
      const project = parseProjectFile(text);
      setPhase({ kind: "ready", project });
    } catch (error) {
      setPhase({ kind: "error", message: error instanceof Error ? error.message : "That file would not parse." });
    }
  };

  const forgetRemembered = () => {
    try {
      window.localStorage.removeItem(REMEMBERED_PROJECT_KEY);
    } catch {
      /* Clearing must never fail loudly. */
    }
    setRemembered(null);
    announce(copy.project.clear);
  };

  const ready = phase.kind === "ready" ? phase.project : null;
  const palette = ready?.direction.colour?.systems.find((system) => system.id === ready.direction.selected.paletteSystemId)
    ?? ready?.direction.colour?.systems[0]
    ?? null;

  return (
    <section className="open-shell">
      <header className="page-head">
        <p className="eyebrow">A saved palette</p>
        <h1 className="display-lg">Bring the color back.</h1>
        <p className="page-lede">Open a project you saved here. The palette, roles, source fingerprint, evidence, and preview copy return. The source image does not—and that is rather the point.</p>
      </header>

      <div className="card" style={{ marginTop: "1.6rem" }}>
        <input
          ref={fileRef}
          type="file"
          accept=".colorplease.json,.json,application/json"
          className="visually-hidden"
          aria-label="Choose a Color, Please project file"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void file.text().then(openText);
            event.target.value = "";
          }}
        />
        <div className="button-row">
          <button type="button" className="btn btn--primary" onClick={() => fileRef.current?.click()}>Choose a Color, Please project</button>
          {remembered ? <button type="button" className="btn" onClick={() => openText(remembered)}>Open the one remembered here</button> : null}
          {remembered ? <button type="button" className="btn btn--ghost" onClick={forgetRemembered}>{copy.project.clear}</button> : null}
        </div>
        {phase.kind === "error" ? <p role="alert" className="truth-note" style={{ color: "var(--caution)", marginTop: "0.9rem" }}>This file was refused: {phase.message}</p> : null}
      </div>

      {ready && palette ? (
        <article className="card saved-project-card" style={{ marginTop: "1.2rem" }}>
          <p className="eyebrow">Ready to reopen</p>
          <h2>{palette.name}</h2>
          <div className="saved-palette-strip" aria-label={`${palette.name} color palette`}>
            {Object.values(palette.roles).slice(0, 8).map((role) => <span key={role.role} style={{ background: role.hex }} title={`${role.role}: ${role.hex}`} />)}
          </div>
          <dl className="project-facts">
            <div><dt>Saved</dt><dd>{new Date(ready.updatedAt).toLocaleString()}</dd></div>
            <div><dt>Revision</dt><dd>{ready.revision}</dd></div>
            <div><dt>Color engine</dt><dd>{ready.engineVersions.colour}</dd></div>
          </dl>
          <p className="truth-note">The saved file contains the finished palette and a SHA-256 fingerprint—not the image or its filename. Reopen now; upload the source again only if you want a fresh extraction.</p>
          <button type="button" className="btn btn--primary" onClick={() => restore(ready)}>Open this palette</button>
        </article>
      ) : null}
    </section>
  );
}
